/**
 * ヘッダータイトル (「OBA-Core 放送地点マスタ」) からトップページへ戻る操作の
 * 有効 / 無効を、配下のページから制御するための軽量コンテキスト。
 *
 * 背景:
 *   ヘッダータイトルをクリックするとトップページ (線区選択画面) に戻るようにしたが、
 *   放送地点の新規作成 / 編集モーダルを開いている最中に誤クリックすると
 *   入力内容が失われてしまう。
 *   そこでモーダル表示中はページ側から `setBlocked(true)` を呼び、
 *   ヘッダー側でリンクを無効化する。
 *
 * Provider が無い環境 (単体テストで個別コンポーネントを描画した場合など) でも
 * 動くよう、デフォルト値は「ブロックしない / setBlocked は no-op」とする。
 */
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

export interface HomeNavigationGuardValue {
  /** true のときヘッダーからのトップ遷移を無効化する */
  blocked: boolean;
  /** ページ側からブロック状態を設定する */
  setBlocked: (blocked: boolean) => void;
}

const HomeNavigationGuardContext = createContext<HomeNavigationGuardValue>({
  blocked: false,
  setBlocked: () => {
    /* no-op (Provider 未設置時) */
  },
});

export function HomeNavigationGuardProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const [blocked, setBlockedState] = useState(false);
  const setBlocked = useCallback((next: boolean) => setBlockedState(next), []);
  const value = useMemo<HomeNavigationGuardValue>(
    () => ({ blocked, setBlocked }),
    [blocked, setBlocked],
  );
  return (
    <HomeNavigationGuardContext.Provider value={value}>
      {children}
    </HomeNavigationGuardContext.Provider>
  );
}

/** ヘッダー / ページ双方から使うアクセサ。 */
export function useHomeNavigationGuard(): HomeNavigationGuardValue {
  return useContext(HomeNavigationGuardContext);
}
